/* eslint-disable @typescript-eslint/no-empty-function */
import React, { createContext, useContext, useState } from 'react';
import HttpClient from '../../../core/HttpClient';
import getContactById from '../../../domain/use_cases/contacts/getContactById';
import Subscription from '../../../domain/models/Subscription';
import getSubscriptionByApiKey from '../../../domain/use_cases/subscriptions/getSubscriptionByApiKey';
import getCompanyByApiKey from '../../../domain/use_cases/companies/getCompanyByApiKey';
import UserDoc from '../../../domain/models/UserDoc';
import getUsersBySubscriptionId from '../../../domain/use_cases/users/getUsersBySubscriptionId';
import * as XLSX from 'xlsx';
import searchContacts from '../../../domain/use_cases/contacts/searchContacts';
import deleteContactsNotAddressed, { DeleteContactsNotAddressedFailure } from '../../../domain/use_cases/contacts/deleteContactsNotAddressed';
import { formatErrorMessage } from '../../../core/utils/errorUtils';
import launchContactsActionsGroup, { ContactActionPayload, LaunchContactsActionsGroupFailure } from '../../../domain/use_cases/contacts/launchContactsActionsGroup';
import deleteContacts, { DeleteContactsFailure } from '../../../domain/use_cases/contacts/deleteContacts';
import AuthenticationContext from '../auth/AuthenticationContext';
import { ContextStatus } from '@leedpulse-monorepo/enums';
import { Authentication } from '@leedpulse-monorepo/classes';
import { ICompany, IContact } from '@leedpulse-monorepo/interfaces';

interface ExportResponse {
  success: boolean;
  message: string;
}

export const CONTACTS_HITS_PER_PAGE = 20;

const ContactsContext = createContext({
  contacts: [] as IContact[],
  contactsCount: 0,
  page: 0,
  hitsPerPage: CONTACTS_HITS_PER_PAGE,
  lastQuery: '',

  search: async (_query: string, _page?: number, _hitsPerPage?: number, _filters?: Record<string, unknown>) => { },
  refresh: async () => { },

  selected: null as IContact | null,
  selectionSubscription: null as Subscription | null,
  // A contact without a number is parked on its company's api_key: no subscription, its company instead (LEED-2463)
  selectionCompany: null as ICompany | null,
  selectionOwners: [] as UserDoc[],
  fetchAndSelectById: async (_: string) => { },
  fetchOwnersById: async (_: string) => { },
  select: (_: IContact) => { },

  exportContacts: async (_authentication: Authentication | null, _subscriptionId: string | null): Promise<ExportResponse> => ({ success: false, message: '' }),
  launchActionGroup: async (_authentication: Authentication | null, _payload: ContactActionPayload): Promise<{ status: ContextStatus; errorMessage?: string }> => ({ status: ContextStatus.IDLE }),
  removeContactsNotAddressed: async (_authentication: Authentication | null, _apiKey: string) => { },
  deleteSelected: async (_authentication: Authentication | null, _apiKey: string, _contactIds: string[]): Promise<{ status: ContextStatus; errorMessage?: string }> => ({ status: ContextStatus.IDLE }),

  deleteResults: { nbContacts: 0, nbJobs: 0, nbSms: 0 },

  status: ContextStatus.IDLE,
  selectionStatus: ContextStatus.IDLE,
  launchActionStatus: ContextStatus.IDLE,
  errorMessage: '',

  reset: () => { },
});

export const ContactsProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const authContext = useContext(AuthenticationContext);

  // Listing state (Algolia-based)
  const [contacts, setContacts] = useState<IContact[]>([]);
  const [contactsCount, setContactsCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [lastQuery, setLastQuery] = useState('');
  const [lastHitsPerPage, setLastHitsPerPage] = useState(CONTACTS_HITS_PER_PAGE);
  const [lastFilters, setLastFilters] = useState<Record<string, unknown>>({});

  // Selection state (detail page)
  const [selected, setSelected] = useState<IContact | null>(null);
  const [selectionSubscription, setSelectionSubscription] = useState<Subscription | null>(null);
  const [selectionCompany, setSelectionCompany] = useState<ICompany | null>(null);
  const [selectionOwners, setSelectionOwners] = useState<UserDoc[]>([]);

  // Delete results
  const [deleteResults, setDeleteResults] = useState({ nbContacts: 0, nbJobs: 0, nbSms: 0 });

  // Error & status
  const [errorMessage, setErrorMessage] = useState('');
  const [status, setStatus] = useState(ContextStatus.IDLE);
  const [selectionStatus, setSelectionStatus] = useState(ContextStatus.IDLE);
  const [launchActionStatus, setLaunchActionStatus] = useState(ContextStatus.IDLE);

  const search = async (query: string, page = 0, hitsPerPage = CONTACTS_HITS_PER_PAGE, filters?: Record<string, unknown>) => {
    setStatus(ContextStatus.LOADING);
    try {
      const appliedFilters = filters ?? lastFilters;
      const useCaseResponse = await searchContacts(authContext.authentication, query, page, hitsPerPage, appliedFilters);
      setContacts(useCaseResponse.hits);
      setContactsCount(useCaseResponse.nbHits);
      setCurrentPage(page);
      setLastQuery(query);
      setLastHitsPerPage(hitsPerPage);
      if (filters !== undefined) setLastFilters(filters);
      setStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setStatus(ContextStatus.ERROR);
    }
  };

  const refresh = async () => {
    await search(lastQuery, currentPage, lastHitsPerPage, lastFilters);
  };

  const exportContacts = async (auth: Authentication | null, subscriptionId: string | null) => {
    if (!auth) {
      return {
        success: false,
        message: 'Authentication is required to export contacts.',
      };
    }

    try {
      const response = await HttpClient.post(
        '/contacts/export',
        {
          export: true,
        },
        auth.token,
        false,
        {
          'x-subscription-id': subscriptionId,
        }
      );

      if (response.success && response.statusReponse === "OK") {
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(response.data), 'contacts');
        XLSX.writeFile(wb, 'Export-Contacts-' + subscriptionId + '-' + new Date().getTime() + '.xlsx');

        return {
          success: true,
          message: 'Les contacts ont bien été exportés.',
        };
      } else {
        return {
          success: false,
          message: 'Une erreur est survenue lors de l\'export des contacts. Veuillez réessayer ultérieurement.',
        };
      }
    } catch (error) {
      return {
        success: false,
        message: 'Une erreur est survenue lors de l\'export des contacts. Veuillez réessayer ultérieurement.',
      };
    }
  };

  const fetchAndSelectById = async (id: string) => {
    setSelectionStatus(ContextStatus.LOADING);
    setErrorMessage('');
    try {
      const useCaseResponse = await getContactById(id);
      if (!useCaseResponse) {
        // Chemin muet jusqu'ici : la page restait en squelette sans une ligne
        // en console. Le cas arrive des que l'id de l'URL ne pointe plus sur un
        // document (contact supprime, ou index de recherche en retard).
        setErrorMessage(`Contact « ${id} » introuvable dans Firestore.`);
        setSelectionStatus(ContextStatus.ERROR);
        return;
      }

      const subscriptionResponse = await getSubscriptionByApiKey(
        useCaseResponse.id_api_key
      );
      if (!subscriptionResponse) {
        // Contact sans numéro (LEED-2463) : garé sur la clé de sa company, la ligne de son owner est partie sans lui
        const company = useCaseResponse.id_api_key ? await getCompanyByApiKey(useCaseResponse.id_api_key) : null;
        if (company) {
          setSelected(useCaseResponse);
          setSelectionSubscription(null);
          setSelectionCompany(company);
          setSelectionOwners([]);
          setSelectionStatus(ContextStatus.SUCCESS);
          return;
        }
        setErrorMessage(
          `Aucun abonnement pour la clé API « ${useCaseResponse.id_api_key} » du contact.`
        );
        setSelectionStatus(ContextStatus.ERROR);
        return;
      }

      const ownersResponse = await getUsersBySubscriptionId(
        subscriptionResponse.id
      );
      setSelected(useCaseResponse);
      setSelectionSubscription(subscriptionResponse);
      setSelectionCompany(null);
      setSelectionOwners(ownersResponse);
      setSelectionStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setErrorMessage(formatErrorMessage((e as Error)?.message));
      setSelectionStatus(ContextStatus.ERROR);
    }
  };

  const fetchOwnersById = async (id: string) => {
    try {
      const ownersResponse = await getUsersBySubscriptionId(id);
      setSelectionOwners(ownersResponse);
      setSelectionStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setSelectionStatus(ContextStatus.ERROR);
    }
  };

  const removeContactsNotAddressed = async (auth: Authentication | null, apiKey: string) => {
    if (!auth) {
      return;
    }
    setStatus(ContextStatus.LOADING);
    try {
      const useCaseResponse = await deleteContactsNotAddressed(apiKey, auth);
      if (useCaseResponse) {
        setDeleteResults({
          nbContacts: useCaseResponse.data.nbContacts,
          nbJobs: useCaseResponse.data.nbJobs,
          nbSms: useCaseResponse.data.nbSms
        });
      }
      setStatus(ContextStatus.SUCCESS);
    } catch (e) {
      if (e instanceof DeleteContactsNotAddressedFailure) {
        setErrorMessage(formatErrorMessage(e.message));
      }
      setStatus(ContextStatus.ERROR);
    }
  };

  const launchActionGroup = async (auth: Authentication | null, payload: ContactActionPayload): Promise<{ status: ContextStatus; errorMessage?: string }> => {
    setLaunchActionStatus(ContextStatus.LOADING);
    if (!auth) {
      const errorMsg = 'Authentication requise';
      setLaunchActionStatus(ContextStatus.ERROR);
      setErrorMessage(errorMsg);
      return { status: ContextStatus.ERROR, errorMessage: errorMsg };
    }
    try {
      await launchContactsActionsGroup(auth, payload);
      setLaunchActionStatus(ContextStatus.SUCCESS);
      return { status: ContextStatus.SUCCESS };
    } catch (e) {
      let errorMsg = '';
      if (e instanceof LaunchContactsActionsGroupFailure) {
        errorMsg = formatErrorMessage(e.message);
        setErrorMessage(errorMsg);
      }
      setLaunchActionStatus(ContextStatus.ERROR);
      return { status: ContextStatus.ERROR, errorMessage: errorMsg };
    }
  };

  const deleteSelected = async (auth: Authentication | null, apiKey: string, contactIds: string[]): Promise<{ status: ContextStatus; errorMessage?: string }> => {
    if (!auth) {
      return { status: ContextStatus.ERROR, errorMessage: 'Authentication requise' };
    }
    setStatus(ContextStatus.LOADING);
    try {
      await deleteContacts(auth, apiKey, contactIds);
      setStatus(ContextStatus.SUCCESS);
      return { status: ContextStatus.SUCCESS };
    } catch (e) {
      let errorMsg = '';
      if (e instanceof DeleteContactsFailure) {
        errorMsg = formatErrorMessage(e.message);
        setErrorMessage(errorMsg);
      }
      setStatus(ContextStatus.ERROR);
      return { status: ContextStatus.ERROR, errorMessage: errorMsg };
    }
  };

  const select = (contact: IContact) => {
    setSelected(contact);
    setSelectionStatus(ContextStatus.SUCCESS);
  };

  const reset = () => {
    setContacts([]);
    setContactsCount(0);
    setCurrentPage(0);
    setLastQuery('');
    setLastHitsPerPage(CONTACTS_HITS_PER_PAGE);
    setLastFilters({});
    setSelected(null);
    setSelectionSubscription(null);
    setSelectionCompany(null);
    setSelectionOwners([]);
    setDeleteResults({ nbContacts: 0, nbJobs: 0, nbSms: 0 });
    setErrorMessage('');
    setStatus(ContextStatus.IDLE);
    setSelectionStatus(ContextStatus.IDLE);
    setLaunchActionStatus(ContextStatus.IDLE);
  };

  return (
    <ContactsContext.Provider
      value={{
        contacts,
        contactsCount,
        page: currentPage,
        hitsPerPage: lastHitsPerPage,
        lastQuery,
        search,
        refresh,
        selected,
        selectionSubscription,
        selectionCompany,
        selectionOwners,
        fetchAndSelectById,
        fetchOwnersById,
        select,
        exportContacts,
        launchActionGroup,
        removeContactsNotAddressed,
        deleteSelected,
        deleteResults,
        status,
        selectionStatus,
        launchActionStatus,
        errorMessage,
        reset,
      }}
    >
      {children}
    </ContactsContext.Provider>
  );
};

export default ContactsContext;
